"use client";

import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { getLocaleFromPathname, defaultLocale, type Locale } from "@/lib/i18n";
import { getPageTranslation } from "@/lib/translations";

// Import industry images
import maschinenbauImg from "@/assets/industries/maschinenbau.jpg";
import medizintechnikImg from "@/assets/industries/medizintechnik.jpg";
import energieImg from "@/assets/industries/energie.jpg";
import foodBeverageImg from "@/assets/industries/food-beverage.jpg";
import transportationImg from "@/assets/industries/transportation.jpg";
import erneuerbareImg from "@/assets/industries/erneuerbare.jpg";

interface CrossIndustrySolutionsSectionProps {
  locale?: Locale;
}

export function CrossIndustrySolutionsSection({ locale: propLocale }: CrossIndustrySolutionsSectionProps) {
  const location = useLocation();
  const locale = propLocale || getLocaleFromPathname(location.pathname);
  const pt = getPageTranslation(locale);

  const industries = [
    { name: pt.crossIndustrySolutions.industries.maschinenbau, image: maschinenbauImg },
    { name: pt.crossIndustrySolutions.industries.medizintechnik, image: medizintechnikImg },
    { name: pt.crossIndustrySolutions.industries.energie, image: energieImg },
    { name: pt.crossIndustrySolutions.industries.foodBeverage, image: foodBeverageImg },
    { name: pt.crossIndustrySolutions.industries.transportation, image: transportationImg },
    { name: pt.crossIndustrySolutions.industries.erneuerbare, image: erneuerbareImg },
  ];

  return (
    <section className="pt-28 md:pt-40 pb-16 md:pb-24 bg-white dark:bg-background">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide mb-4">
            {pt.crossIndustrySolutions.title}
          </h2>
          
          {/* Intro Text */}
          <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide max-w-5xl mx-auto">
            {pt.crossIndustrySolutions.description}
          </p>
        </motion.div>
          
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 justify-items-center max-w-5xl mx-auto">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative rounded-xl overflow-hidden w-full aspect-[4/3] group cursor-pointer"
              >
                {/* Background Image */}
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-300" />
                
                {/* Text Content */}
                <div className="absolute inset-0 flex items-end p-4 md:p-6">
                  <h3 className="font-bold tracking-wide text-xs md:text-base text-white drop-shadow-lg break-words hyphens-auto">
                    {industry.name.includes('/') ? (
                      <>
                        {industry.name.split('/')[0]}/
                        <br />
                        {industry.name.split('/').slice(1).join('/')}
                      </>
                    ) : industry.name}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide text-center mt-10 max-w-5xl mx-auto"
        >
          {pt.crossIndustrySolutions.moreIndustries}
        </motion.p>
      </div>
    </section>
  );
}
